import React, { useEffect, useState } from 'react';
import { FileDown, Snowflake } from 'lucide-react';
import { estoqueDoCliente } from '../../../data/estoque';
import {
  saldoDeEmbrioes, saldoDeSemen, resumoDoEstoqueDoCliente, extratoDeEstoque,
  temDataAproximada,
} from '../../../utils/calculations/extratoDeEstoque';
import { gerarExtratoDeEstoquePDF } from '../../../utils/estoquePDF';

/**
 * O QUE ESTE CLIENTE TEM GUARDADO, na tela do laboratório.
 *
 * A pergunta chega por telefone — "quantos embriões meus estão aí?", "sobrou
 * sêmen do CABARÉ?" — e a resposta existia em dois lugares, nenhum deles o do
 * cliente: a aba de Estoque, que lista o botijão inteiro e obriga a procurar o
 * nome no meio, e o portal, que só o cliente enxerga. Quem atende o telefone
 * ficava sem tela.
 *
 * CARREGA SÓ QUANDO ABRE. São quatro consultas por cliente, e a carteira tem
 * catorze linhas: buscar tudo de todos na montagem da aba seria cinquenta e seis
 * consultas para responder a uma pergunta sobre um.
 *
 * O ENDEREÇO DA PALHETA não aparece, aqui nem no PDF. Ele é mapa interno, muda
 * quando alguém arruma o botijão, e quem precisa dele para ir buscar usa a aba
 * de Estoque, que é onde ele mora.
 */

const num = (v) => Number(v || 0).toLocaleString('pt-BR');

const doses = (v) => {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
};

const dataCurta = (iso) => {
  const [a, m, d] = String(iso || '').slice(0, 10).split('-');
  return a ? `${d}/${m}/${a}` : '—';
};

const EstoqueDoCliente = ({ client, laboratoryId, laboratorio }) => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [verExtrato, setVerExtrato] = useState(false);

  useEffect(() => {
    if (!client?.id || !laboratoryId) return undefined;
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    estoqueDoCliente(laboratoryId, client.id)
      .then(d => { if (!cancelado) setDados(d); })
      .catch(e => {
        console.error('Estoque do cliente:', e);
        // Sem cópia local: um saldo velho apresentado como atual faz alguém
        // prometer ao telefone um embrião que já saiu.
        if (!cancelado) setErro(e.message);
      })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
  }, [client?.id, laboratoryId]);

  if (carregando) {
    return <p className="text-xs text-slate-500 py-2">Carregando o estoque…</p>;
  }
  if (erro) {
    return (
      <p className="text-xs text-slate-600 py-2">
        Não foi possível ler o estoque deste cliente: {erro}
      </p>
    );
  }

  const r = resumoDoEstoqueDoCliente(dados);
  const embrioes = saldoDeEmbrioes(dados?.congelados);
  const semen = saldoDeSemen(dados?.partidas);
  const extrato = extratoDeEstoque(dados);

  // Nada guardado E nada movimentado é cliente sem história de estoque: a seção
  // some inteira, em vez de ocupar a linha com dois zeros.
  if (r.vazio && extrato.length === 0) {
    return <p className="text-xs text-slate-400 py-2">Nada guardado no laboratório.</p>;
  }

  return (
    <div className="py-2 space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Snowflake size={13} /> Guardado no laboratório
        </span>

        {r.embrioes > 0 && (
          <span className="text-sm text-slate-800">
            <span className="font-semibold tabular-nums">{num(r.embrioes)}</span>
            {' '}{r.embrioes === 1 ? 'embrião' : 'embriões'}
          </span>
        )}
        {r.doses > 0 && (
          <span className="text-sm text-slate-800">
            <span className="font-semibold tabular-nums">{doses(r.doses)}</span>
            {' '}dose{r.doses === 1 ? '' : 's'} de sêmen
          </span>
        )}

        <button
          type="button"
          onClick={() => gerarExtratoDeEstoquePDF(dados, { cliente: client, laboratorio })}
          className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
          title="Gerar o extrato de estoque deste cliente em PDF"
        >
          <FileDown size={13} /> Extrato em PDF
        </button>
      </div>

      {embrioes.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1">Embriões congelados</p>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded bg-white">
            {embrioes.map(g => (
              <li key={`${g.doadora}-${g.touro}`} className="px-2.5 py-1.5 flex items-baseline gap-2 text-sm">
                <span className="text-slate-800 min-w-0 truncate">
                  {g.doadora} <span className="text-slate-400">×</span> {g.touro}
                </span>
                {g.estagios.length > 0 && (
                  <span className="text-xs text-slate-400 truncate">{g.estagios.join(', ')}</span>
                )}
                <span className="ml-auto font-semibold tabular-nums text-slate-900">{num(g.quantidade)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {semen.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1">Doses de sêmen</p>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded bg-white">
            {semen.map(p => (
              <li key={p.semen_batch_id} className="px-2.5 py-1.5 flex items-baseline gap-2 text-sm">
                <span className="text-slate-800 min-w-0 truncate">{p.touro}</span>
                {p.codigo && <span className="text-xs text-slate-400">lote {p.codigo}</span>}
                <span className="ml-auto font-semibold tabular-nums text-slate-900">{doses(p.doses)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* O EXTRATO FICA FECHADO. O saldo responde a pergunta; o extrato existe
          para defender o saldo quando ele não bate com o que o cliente lembra —
          e aberto por padrão empurraria o saldo para fora da tela. */}
      {extrato.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setVerExtrato(v => !v)}
            className="text-xs text-slate-600 hover:text-slate-900"
          >
            {verExtrato ? 'Esconder' : 'Ver'} o extrato ({extrato.length})
          </button>

          {verExtrato && (
            <ul className="mt-1.5 divide-y divide-slate-100 border border-slate-200 rounded bg-white">
              {extrato.map((l, i) => (
                <li key={`${l.data}-${l.titulo}-${l.descricao}-${i}`}
                    className="px-2.5 py-1.5 flex items-baseline gap-2 text-xs">
                  <span
                    className="text-slate-400 tabular-nums whitespace-nowrap"
                    title={l.aproximada
                      ? 'Data do serviço: a do congelamento não foi registrada nesta palheta'
                      : undefined}
                  >
                    {dataCurta(l.data)}{l.aproximada ? ' *' : ''}
                  </span>
                  <span className="text-slate-800 whitespace-nowrap">{l.titulo}</span>
                  <span className="text-slate-500 min-w-0 truncate">{l.descricao}</span>
                  <span className="ml-auto tabular-nums whitespace-nowrap text-slate-900">
                    {l.tipo === 'entrada' ? '+' : '−'}{' '}
                    {l.material === 'semen' ? `${doses(l.quantidade)} d` : num(l.quantidade)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* A ressalva só aparece quando há o que ressalvar: fixa, ela vira
              moldura e tira a força da frase no dia em que importa. */}
          {verExtrato && temDataAproximada(extrato) && (
            <p className="text-xs text-slate-400 mt-1">
              * data do serviço, porque a do congelamento não foi registrada
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EstoqueDoCliente;
